import React from 'react';

// Payment modes per the CASH/BANK payment-recording spec (SOFTWARRE LAYOUT.xlsx).
export const PAYMENT_MODES = [
  { value: 'CASH', label: 'Cash' },
  { value: 'CHEQUE', label: 'Cheque' },
  { value: 'CASH_DEPOSIT', label: 'Cash Deposit' },
  { value: 'ONLINE_TRANSFER', label: 'Online Transfer' },
  { value: 'TT_FOREIGN_REMITTANCE', label: 'TT (Foreign Remittance)' },
];

const F = ({ label, value, onChange, type = 'text', optional, span }) => (
  <div style={{ gridColumn: span ? '1 / -1' : undefined }}>
    <label style={{ fontSize: 11, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 3 }}>
      {label}{optional && <span style={{ color: 'var(--color-text-tertiary)' }}> (optional)</span>}
    </label>
    <input
      type={type}
      value={value ?? ''}
      onChange={e => onChange(e.target.value)}
      style={{ width: '100%', padding: '6px 8px', border: '1px solid var(--color-border-strong)', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }}
    />
  </div>
);

const Group = ({ title, color = 'var(--color-neutral-700)', children }) => (
  <div style={{ background: 'var(--color-neutral-50)', border: '1px solid var(--color-border-medium)', borderRadius: 8, padding: 12, marginBottom: 10 }}>
    <div style={{ fontSize: 12, fontWeight: 700, color, marginBottom: 8 }}>{title}</div>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>{children}</div>
  </div>
);

/**
 * Mode-specific fields for Record Payment modals (Invoice + Purchase), per SOFTWARRE LAYOUT.xlsx:
 * CASH → issuer/receiver name. BANK → Cheque / Cash Deposit / Online Transfer / TT, each with
 * their own sender + receiver detail blocks. `details` is a flat key/value object; `onChange`
 * receives the whole updated object (merge is done here) so the parent can just hold one piece of state.
 */
const PaymentModeFields = ({ mode, details = {}, onChange }) => {
  const set = (key) => (val) => onChange({ ...details, [key]: val });

  if (!mode || mode === 'CASH') {
    return (
      <Group title="Cash Details">
        <F label="Issuer Name" value={details.issuerName} onChange={set('issuerName')} />
        <F label="Receiver Name" value={details.receiverName} onChange={set('receiverName')} />
      </Group>
    );
  }

  if (mode === 'CHEQUE') {
    return (
      <>
        <Group title="Sender Bank Details">
          <F label="Account No" value={details.senderAccountNo} onChange={set('senderAccountNo')} />
          <F label="Account Name" value={details.senderAccountName} onChange={set('senderAccountName')} />
          <F label="Cheque No" value={details.chequeNo} onChange={set('chequeNo')} span />
        </Group>
        <Group title="Receiver Bank Details" color="var(--color-success)">
          <F label="Account No" value={details.receiverAccountNo} onChange={set('receiverAccountNo')} />
          <F label="Account Name" value={details.receiverAccountName} onChange={set('receiverAccountName')} />
        </Group>
      </>
    );
  }

  if (mode === 'CASH_DEPOSIT') {
    return (
      <>
        <Group title="Sender Bank Details">
          <F label="Account No" value={details.senderAccountNo} onChange={set('senderAccountNo')} />
          <F label="Account Name" value={details.senderAccountName} onChange={set('senderAccountName')} />
          <F label="Deposit Slip No" value={details.depositSlipNo} onChange={set('depositSlipNo')} span />
        </Group>
        <Group title="Receiver Bank Details" color="var(--color-success)">
          <F label="Account No" value={details.receiverAccountNo} onChange={set('receiverAccountNo')} />
          <F label="Account Name" value={details.receiverAccountName} onChange={set('receiverAccountName')} />
        </Group>
      </>
    );
  }

  if (mode === 'ONLINE_TRANSFER') {
    return (
      <>
        <Group title="Sender Bank Details">
          <F label="Account No" value={details.senderAccountNo} onChange={set('senderAccountNo')} />
          <F label="Account Name" value={details.senderAccountName} onChange={set('senderAccountName')} />
          <F label="Online Transfer Ref No" value={details.transferRefNo} onChange={set('transferRefNo')} span />
        </Group>
        <Group title="Receiver Bank Details" color="var(--color-success)">
          <F label="Account No" value={details.receiverAccountNo} onChange={set('receiverAccountNo')} />
          <F label="Account Name" value={details.receiverAccountName} onChange={set('receiverAccountName')} />
        </Group>
      </>
    );
  }

  if (mode === 'TT_FOREIGN_REMITTANCE') {
    return (
      <>
        <Group title="Sender / Remitter Details" color="var(--color-primary)">
          <F label="Remitter Name" value={details.remitterName} onChange={set('remitterName')} />
          <F label="Remitter Bank Name" value={details.remitterBankName} onChange={set('remitterBankName')} />
          <F label="SWIFT Amount" type="number" value={details.swiftAmount} onChange={set('swiftAmount')} />
          <F label="Date of Remittance" type="date" value={details.dateOfRemittance} onChange={set('dateOfRemittance')} />
          <F label="Origin Currency & Amount" value={details.originCurrencyAmount} onChange={set('originCurrencyAmount')} optional />
          <F label="Remitted Currency & Amount" value={details.remittedCurrencyAmount} onChange={set('remittedCurrencyAmount')} />
          <F label="SWIFT Reference No" value={details.swiftRefNo} onChange={set('swiftRefNo')} optional span />
        </Group>
        <Group title="Receiver Details" color="var(--color-success)">
          <F label="Receiver Company Name" value={details.receiverCompanyName} onChange={set('receiverCompanyName')} span />
          <F label="Received Amount" type="number" value={details.receivedAmount} onChange={set('receivedAmount')} />
          <F label="Discount Amount" type="number" value={details.discountAmount} onChange={set('discountAmount')} />
          <F label="International Bank Deductions" type="number" value={details.bankDeductions} onChange={set('bankDeductions')} />
          <F label="Remittance Received Date" type="date" value={details.remittanceReceivedDate} onChange={set('remittanceReceivedDate')} />
          <F label="Currency & Amount" value={details.currencyAmount} onChange={set('currencyAmount')} />
          <F label="MT-103 Ref No" value={details.mt103RefNo} onChange={set('mt103RefNo')} optional />
          <F label="Credit Advise Ref No" value={details.creditAdviseRefNo} onChange={set('creditAdviseRefNo')} optional />
          <F label="Service Charges" type="number" value={details.serviceCharges} onChange={set('serviceCharges')} />
          <F label="Withholding Tax" type="number" value={details.withholdingTax} onChange={set('withholdingTax')} />
          <F label="FED" type="number" value={details.fed} onChange={set('fed')} />
          <F label="Other Charges Amount" type="number" value={details.otherChargesAmount} onChange={set('otherChargesAmount')} />
          <F label="Other Charges Narration" value={details.otherChargesNarration} onChange={set('otherChargesNarration')} span />
          <F label="Total Bank Charges" type="number" value={details.totalBankCharges} onChange={set('totalBankCharges')} />
          <F label="Net Received Amount" type="number" value={details.netReceivedAmount} onChange={set('netReceivedAmount')} />
        </Group>
      </>
    );
  }

  return null;
};

export default PaymentModeFields;
